import React, { useMemo } from "react";
import { ZitateSliderOptionsType } from "@/types/ModuleOptionsType";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
} from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import {
  QuoatesCard,
  QuoatesCardOptions,
} from "../../../components/QuoatesCard/QuoatesCard";
import { EndCard } from "../../../components/EventCard/EventCard";
import { EndCardsType } from "@/types/OptionType";
import { bulmaColumnsToWidth } from "@/utils/layout/columnSize";
import useWindowSize from "@/hooks/useWindowSize";
import {
  QuotesElementType,
  QuoteSliderModuleModuleType,
} from "../QuoteSliderModule";
import styles from "./QuoteSlider.module.scss";
interface ZitateSliderProps {
  headlineTag: QuoteSliderModuleModuleType["quotes_slider"]["headline_html_tag"];
  elements: QuotesElementType[];
  quotesSliderOptions: ZitateSliderOptionsType;
  showAvatar?: boolean;
  showRating?: boolean;
  moduleIndex: number;
  endCardData: EndCardsType | undefined;
  heading_level: number;
}
const useDeviceType = () => {
  const { width } = useWindowSize();

  return useMemo(() => {
    if (width >= 1920) return "ultrahd";
    if (width >= 1408) return "fullhd";
    if (width >= 1216) return "widescreen";
    if (width >= 1024) return "desktop";
    if (width >= 768) return "tablet";
    return "mobile";
  }, [width]);
};

interface BreakpointWidths {
  [key: string]: string; // Index signature for string keys
}

const breakpointOrder = [
  "mobile",
  "tablet",
  "desktop",
  "widescreen",
  "fullhd",
  "ultrahd",
] as const;

const fillMissingBreakpoints = (
  partialWidths: BreakpointWidths,
): BreakpointWidths => {
  const result: BreakpointWidths = {};
  let lastValue = "100%"; // Default value for mobile if not specified

  for (const breakpoint of breakpointOrder) {
    if (partialWidths[breakpoint]) {
      lastValue = partialWidths[breakpoint]!;
      result[breakpoint] = lastValue;
    } else {
      result[breakpoint] = lastValue;
    }
  }

  return result;
};

export const ZitatSlider = ({
  headlineTag,
  elements,
  quotesSliderOptions,
  showAvatar = false,
  showRating = false,
  moduleIndex,
  endCardData,
  heading_level,
}: ZitateSliderProps) => {
  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    slider_options: {
      slider_navigation,
      has_autoplay,
      cards_slider_options: { end_card, show_end_card },
    },
    card_width,
  } = quotesSliderOptions;

  const screenSize = useDeviceType();

  const width_breakpoints: BreakpointWidths = fillMissingBreakpoints(
    card_width
      ? card_width.reduce((prev, curr) => {
          if (!curr.breakpoint) {
            return prev;
          }
          return {
            ...prev,
            [curr.breakpoint]: bulmaColumnsToWidth(curr.number_of_cols),
          };
        }, {})
      : { mobile: "100%", widescreen: "100%" },
  );

  const activeBreakpointValue = width_breakpoints[screenSize];
  const isFull = activeBreakpointValue === "100%";

  return (
    <div className={styles["quote-slider-wrapper"]}>
      <Carousel
        hasFade={true}
        containScroll={false}
        autoplay={has_autoplay}
        align={"start"}
      >
        <CarouselContent
          outerPadding="2.25rem 0"
          spacing={`2rem`}
          overflowVisible={isFull}
        >
          {Array.isArray(elements) &&
            elements.map((element, index) => {
              return (
                <CarouselItem
                  key={"slide-" + index}
                  itemWidth={width_breakpoints}
                  itemHeight={"100%"}
                >
                  <QuoatesCard
                    element={element}
                    quotesOptions={quotesSliderOptions as QuoatesCardOptions}
                    showAvatar={showAvatar}
                    showRating={showRating}
                    cardIndex={index}
                    moduleIndex={moduleIndex}
                    cardType="slider"
                    headling_level={heading_level}
                  />
                </CarouselItem>
              );
            })}
          {show_end_card && end_card && endCardData && endCardData.link && (
            <CarouselItem itemWidth={width_breakpoints}>
              <EndCard
                headlineTag={headlineTag}
                end_card={{
                  background_color: end_card.background_color,
                  button_type: end_card.button_type,
                  link: endCardData.link,
                  title: endCardData.title,
                }}
                show_border_radius={show_border_radius}
                show_box_shadow={show_box_shadow}
                showBox={show_card_box}
              />
            </CarouselItem>
          )}
        </CarouselContent>
        <CarouselNavigation
          iconLabelNext="arrowBoxRight"
          iconLabelPrev="arrowBoxLeft"
          type={slider_navigation}
        />
      </Carousel>
    </div>
  );
};
